import { FC, useContext, useEffect } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'

import { Button } from '../Buttons'
import { FF, handleFormSubmissionError } from '../Form'
import { Icon, IconName } from '../Icon'
import { TextareaInput } from '../InputV2'
import { SidePanelContent, SidePanelContext, SidePanelFooter, SidePanelHeader } from '../SidePanel'
import { Toast } from '../Toast'

import { s } from './styles'

type FeedbackRating = 1 | 5

type CreateFeedbackRequest = {
  rating: FeedbackRating
  contentPage: string
  initiatedAt: string
  description: string
}

class FeedbackFormValues {
  rating: FeedbackRating | null = null
  description: string = ''
}

type FeedbackOnSubmitValidationError = {
  form: string
  fields: Partial<Record<keyof FeedbackFormValues, string>>
}

type FeedbackDialogProps = {
  contentPage: string
  onClose: () => void
}

const isFeedbackRating = (value: FeedbackFormValues['rating']): value is FeedbackRating => value === 1 || value === 5

const validateFeedbackOnSubmit = ({
  value
}: {
  value: FeedbackFormValues
}): FeedbackOnSubmitValidationError | undefined => {
  if (!isFeedbackRating(value.rating)) {
    return {
      form: 'Please select an option to proceed.',
      fields: { rating: 'Please select an option to proceed.' }
    }
  }

  if (value.rating === 1 && !value.description) {
    return {
      form: 'Please tell us what was difficult or confusing before submitting.',
      fields: { description: 'Please tell us what was difficult or confusing before submitting.' }
    }
  }

  return undefined
}

export const FeedbackPanelContent: FC<FeedbackDialogProps> = ({ contentPage, onClose }) => {
  const formApi = useForm({
    defaultValues: new FeedbackFormValues(),
    onSubmit: ({ value: { rating, description } }) => {
      mutateAsync({
        rating: Number(rating) as FeedbackRating,
        description,
        contentPage,
        initiatedAt: new Date().toISOString()
      })
    },
    validators: {
      onSubmit: validateFeedbackOnSubmit
    }
  })

  const { mutateAsync, isPending } = useMutation<void, Error, CreateFeedbackRequest>({
    mutationFn: (props) => Request.post(Request.urls.bank.feedback, props),
    onSuccess: () => {
      onClose()
      Toast.success('Thank you', 'Your feedback will help us improve this page')
    },
    onError: async (error) => {
      handleFormSubmissionError(error, formApi)
    }
  })

  const context = useContext(SidePanelContext)

  useEffect(() => {
    context.block(isPending)
  }, [context, isPending])

  return (
    <>
      <SidePanelHeader>Help Us Improve</SidePanelHeader>
      <SidePanelContent>
        <s.DialogContent>
          <s.Title>Is this page helpful and easy to use?</s.Title>

          <s.RatingButtonsWrapper>
            <formApi.Field name="rating">
              {(field) => (
                <>
                  <s.RatingButton
                    $selected={field.state.value === 5}
                    onClick={() => field.handleChange(5)}
                    data-qa="feedback-rating-positive"
                  >
                    <Icon name={IconName.FEEDBACK_THUMB_GOOD} size={40} />
                  </s.RatingButton>
                  <s.RatingButton
                    $selected={field.state.value === 1}
                    onClick={() => field.handleChange(1)}
                    data-qa="feedback-rating-negative"
                  >
                    <Icon name={IconName.FEEDBACK_THUMB_BAD} size={40} />
                  </s.RatingButton>
                </>
              )}
            </formApi.Field>
          </s.RatingButtonsWrapper>
          <s.ReactionTextWrapper>
            <formApi.Subscribe
              children={({ values, fieldMeta }) => {
                const ratingError = fieldMeta.rating?.errors.find((error): error is string => typeof error === 'string')

                if (ratingError) {
                  return <s.ReactionErrorText>{ratingError}</s.ReactionErrorText>
                }

                const ratingLabels = {
                  1: 'Sorry it was a poor experience.',
                  5: 'Glad it worked well for you.'
                } satisfies Record<FeedbackRating, string>
                return values.rating != null ? <s.ReactionText>{ratingLabels[values.rating]}</s.ReactionText> : null
              }}
            />
          </s.ReactionTextWrapper>
          <formApi.Field name="description">
            {(field) => (
              <FF
                field={field}
                input={(inputProps) => (
                  <TextareaInput
                    {...inputProps}
                    required={useStore(formApi.store, ({ values }) => values.rating) === 1}
                    rows={5}
                    maxLength={255}
                    hint="What was difficult or confusing on this page?"
                    hideHint={Boolean(inputProps?.error)}
                    data-qa="feedback-description"
                  />
                )}
              />
            )}
          </formApi.Field>
        </s.DialogContent>
      </SidePanelContent>
      <SidePanelFooter>
        <s.FooterButtons>
          <Button onClick={() => context.close()}>Cancel</Button>
          <Button face="primary" onClick={formApi.handleSubmit} loading={isPending} data-qa="feedback-submit">
            Submit Feedback
          </Button>
        </s.FooterButtons>
      </SidePanelFooter>
    </>
  )
}
